/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
import { useDispatch, useSelector } from 'react-redux'; // Importing hooks for Redux dispatch and selector
import {useEffect, useState, useRef} from 'react'; // Importing React hooks
import { useTranslation } from 'react-i18next';
import {calculateHeight, errorMsg, isSmWidth} from "../../../../../../../Apis/functions";
import routes from "../../../../../../../Routes/routes";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {STATUS_MODEL} from "../../../../../../../enums/statusEnum";
import {
    GetAttendanceAdjustmentGroupById,
    GetAttendancePeriodRequest
} from "../../../../../../../reduxToolkit/AttendanceAdjustment/AttendanceAdjustmentApi";
import {FILE_MODEL} from "../../../../../Visitor/Visitor/Model/Model";

const RequestedBatchApprovalApp = () => {
    const navigate = useNavigate(); // Initialize navigate function for routing
    const { t } = useTranslation(); // Translation hook to handle language translations
    const dispatch = useDispatch(); // Redux dispatch hook
    const { id } = useParams();
    const location = useLocation();
    const { state } = location; // Get state from location
    const DEV = useSelector((state) => state?.CommonsSlice?.development); // Accessing development data from Redux store
    const { permission } = useSelector((state) => state.authenticatioauthennSlice); // Accessing user permissions from Redux store
    const { isAttendanceSupervisor } = useSelector((state) => state.AttendancePersistSlice);
    const [modified, setModified] = useState(false);
    const containerCardRef = useRef(null);
    const containerTableRef = useRef(null);
    const contentCardRef = useRef(null);
    const contentTableRef = useRef(null);
    const [item, setItem] = useState(null);
    const [showApprove, setShowApprove] = useState(false);
    const [showDenied, setShowDenied] = useState(false);
    const [showIssueComment, setShowIssueComment] = useState(false);
    const [row, setRow] = useState(null);
    const [evidence, setEvidence] = useState([]);
    const showBadge = (item?.approvalStatusId && item?.approvalStatusId !== STATUS_MODEL.PENDING_REVIEW);

    // Generic modal configuration for approve/deny/request changes actions
    const genericCommentModals = [
        {
            show: showApprove,
            setter: setShowApprove,
            title: 'approve_request',
            setRow: setRow,
            row: row,
            status: STATUS_MODEL.APPROVED,
            requiredComment: false
        },
        {
            show: showDenied,
            setter: setShowDenied,
            title: 'decline_comment',
            setRow: setRow,
            row: row,
            status: STATUS_MODEL.DENIED,
            requiredComment: true
        },
        {
            show: showIssueComment,
            setter: setShowIssueComment,
            title: 'request_changes',
            setRow: setRow,
            row: row,
            status: STATUS_MODEL.CHANGES_REQUESTED,
            requiredComment: true
        },
    ];

    /**
     * Effect: Adjusts dynamic container heights based on screen size and badge visibility.
     *
     * - Skips execution on small screens.
     * - Calculates height for card and table containers.
     * - Re-runs when `showBadge` changes.
     */
    useEffect(() => {
        if (isSmWidth()) return;
        const bottom = showBadge ? 80 : 70;
        calculateHeight({elementRef: containerCardRef, bottom:bottom});
        calculateHeight({elementRef: containerTableRef, bottom:bottom});
        calculateHeight({elementRef: contentCardRef, bottom:bottom});
        calculateHeight({elementRef: contentTableRef, bottom:bottom});
    }, [showBadge]);

    /**
     * Effect: Loads attendance period configuration on component mount.
     *
     * - Calls API to retrieve attendance period request configuration.
     * - If successful, triggers `getInfo` to fetch batch details.
     * - Handles error response using `errorMsg`.
     */
    useEffect(() => {
        dispatch(GetAttendancePeriodRequest()).then(
            ({
                 payload: {
                     data: { success, code },
                 }
             }) => {
                if (success){
                    getInfo();
                }else errorMsg(code);
            });
    }, []); // Dependency array to trigger re-fetching on update

    /**
     * Effect: Re-fetches batch information when `modified` flag changes.
     *
     * - If `modified` is true:
     *   - Calls `getInfo` to refresh data
     *   - Resets `modified` flag
     */
    useEffect(() => {
        if(modified){
            getInfo();
            setModified(false);
        }
    }, [modified]); // Dependency array to trigger re-fetching on update

    /**
     * Fetches batch approval data by ID.
     *
     * - Calls API to retrieve attendance adjustment group details.
     * - Updates:
     *   - `item` with batch information
     *   - `evidence` with mapped file objects
     *
     * - Handles API errors using `errorMsg`.
     *
     * @function getInfo
     */
    const getInfo = () => {
        dispatch(GetAttendanceAdjustmentGroupById(id)).then(
            ({
                 payload: {
                     data: { data, success, code },
                 }
             }) => {
                if (success) {
                    setItem(data);
                    const aux = data?.evidence?.map(e => FILE_MODEL.set(e));
                    if(aux) setEvidence(aux);
                }else errorMsg(code);
            }
        );
    };

    /**
     * Handles navigation back to the previous screen.
     *
     * - If `backUrl` exists in navigation state:
     *   - Navigates back preserving tab and subview state
     * - Otherwise:
     *   - Redirects to attendance main screen
     *
     * @function goBack
     */
    const goBack = () => {
        if(state?.backUrl){
            navigate(state.backUrl, {
                state: {
                    currentTab: state?.currentTab,
                    currentSubView: state?.currentSubView
                }
            });
        } else {
            navigate(routes.attendance.main)
        }
    }

    return {
        t,
        id,
        dispatch,
        navigate,
        DEV,
        permission,
        contentCardRef,
        contentTableRef,
        containerCardRef,
        containerTableRef,
        item,
        setModified,
        goBack,
        setShowApprove,
        setShowDenied,
        setShowIssueComment,
        setRow,
        genericCommentModals,
        evidence,
        showBadge,
        isAttendanceSupervisor
    };
};

export default RequestedBatchApprovalApp; // Exporting the component for use in other parts of the app
